/**
 * resellerCap — read-side aggregation for the Play 13 admin disk-cap cards.
 *
 * Reads `usage_snapshot` (populated daily by `usage-snapshot-runner`)
 * and shapes data for `AdminServerHealthPage`. RLS gates staff-only access.
 *
 * One round-trip:
 *   - Get the latest snapshot_date
 *   - Pull all rows for that date
 *   - Compute totals + alarms client-side
 *
 * NOTE: bandwidth + inode aggregation is deferred to Sprint C (E.1 / E.2).
 * v1 surfaces disk only. Bandwidth gauge card will render a placeholder
 * until the columns are populated.
 */

import { supabase } from '@/shared/lib/supabase';
import { RESELLER_SUBSTRATE } from '@/config/resellerSubstrate';

export interface ResellerCapAccount {
  customer_link_id: string;
  cpanel_username: string;
  customer_id: string | null;
  customer_name: string | null;
  primary_domain: string | null;
  service_plan_name: string | null;
  disk_used_mb: number | null;
  disk_quota_mb: number | null;
  /** % of own quota (0–100). null when quota or used is null. */
  pct_of_quota: number | null;
  /** % of total reseller physical cap (0–100). */
  pct_of_cap: number | null;
  /** Sprint C C-2 — inode usage from WHM accountsummary. NULL = not yet populated. */
  inodes_used: number | null;
  inodes_quota: number | null;
  /** % of inode quota (0–100). null when either field is null. */
  pct_of_inode_quota: number | null;
}

export interface ResellerCapTotals {
  disk_used_mb: number;
  disk_allocated_mb: number;
  account_count: number;
  pct_of_disk_cap: number;
  /** disk_allocated_mb / plan_disk_cap_mb — > 1 means oversold. */
  overselling_ratio: number;
}

export interface ResellerCapAlarms {
  /** Total disk used as % > 60 / > 80 / OK. */
  disk_warning: boolean;
  disk_critical: boolean;
  /** cpanel_usernames using more than singleAccountWarnMb. */
  accounts_over_threshold: string[];
  /** Sprint C C-2 — cpanel_usernames using more than 70% of their inode quota. */
  accounts_over_inode_threshold: string[];
}

export interface ResellerCapSnapshot {
  snapshot_date: string;
  is_stale: boolean;
  totals: ResellerCapTotals;
  alarms: ResellerCapAlarms;
  accounts: ResellerCapAccount[];
}

interface SnapshotRow {
  customer_link_id: string;
  cpanel_username: string;
  disk_used_mb: number | null;
  disk_quota_mb: number | null;
  inodes_used: number | null;
  inodes_quota: number | null;
}

interface JoinRow extends SnapshotRow {
  /** Supabase returns the FK-joined row as an array even on N:1 — unwrap [0]. */
  customer_link:
    | { customer_id: string | null; primary_domain: string | null }
    | Array<{ customer_id: string | null; primary_domain: string | null }>
    | null;
}

function unwrapLink(link: JoinRow['customer_link']): { customer_id: string | null; primary_domain: string | null } | null {
  if (!link) return null;
  if (Array.isArray(link)) return link[0] ?? null;
  return link;
}

export const resellerCapService = {
  /**
   * Load the most recent usage_snapshot rollup + compute totals/alarms.
   *
   * Returns null if usage_snapshot is empty (e.g. fresh deploy before first
   * cron run). UI should render a "no data yet — first snapshot lands at
   * 03:30 SAST" message in that case.
   */
  async loadLatest(): Promise<ResellerCapSnapshot | null> {
    // Step 1: most recent snapshot_date
    const { data: latest, error: dateErr } = await supabase
      .from('usage_snapshot')
      .select('snapshot_date')
      .order('snapshot_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (dateErr) throw new Error(`Could not load latest snapshot: ${dateErr.message}`);
    if (!latest?.snapshot_date) return null;

    // Step 2: all rows for that date, joined with customer_link for domain + customer_id.
    // Sprint C C-2: also select inodes_used / inodes_quota.
    const { data: rows, error: rowsErr } = await supabase
      .from('usage_snapshot')
      .select(
        'customer_link_id, cpanel_username, disk_used_mb, disk_quota_mb, inodes_used, inodes_quota, customer_link(customer_id, primary_domain)',
      )
      .eq('snapshot_date', latest.snapshot_date);
    if (rowsErr) throw new Error(`Could not load snapshot rows: ${rowsErr.message}`);

    const cap = RESELLER_SUBSTRATE.currentPlan.diskCapMb;
    const alarms = RESELLER_SUBSTRATE.alarms;

    const accounts: ResellerCapAccount[] = ((rows ?? []) as unknown as JoinRow[]).map((r) => {
      const used = r.disk_used_mb;
      const quota = r.disk_quota_mb;
      const link = unwrapLink(r.customer_link);
      const inodesUsed = r.inodes_used;
      const inodesQuota = r.inodes_quota;
      return {
        customer_link_id: r.customer_link_id,
        cpanel_username: r.cpanel_username,
        customer_id: link?.customer_id ?? null,
        customer_name: null, // Filled by caller if needed via a 2nd query — keep this fast
        primary_domain: link?.primary_domain ?? null,
        service_plan_name: null,
        disk_used_mb: used,
        disk_quota_mb: quota,
        pct_of_quota: used !== null && quota !== null && quota > 0 ? (used / quota) * 100 : null,
        pct_of_cap: used !== null && cap > 0 ? (used / cap) * 100 : null,
        inodes_used: inodesUsed,
        inodes_quota: inodesQuota,
        pct_of_inode_quota:
          inodesUsed !== null && inodesQuota !== null && inodesQuota > 0
            ? (inodesUsed / inodesQuota) * 100
            : null,
      };
    });

    const totalUsed = accounts.reduce((sum, a) => sum + (a.disk_used_mb ?? 0), 0);
    const totalAllocated = accounts.reduce(
      (sum, a) => sum + (a.disk_quota_mb && a.disk_quota_mb > 0 ? a.disk_quota_mb : 0),
      0,
    );

    const pctOfDiskCap = cap > 0 ? (totalUsed / cap) * 100 : 0;

    const totals: ResellerCapTotals = {
      disk_used_mb: totalUsed,
      disk_allocated_mb: totalAllocated,
      account_count: accounts.length,
      pct_of_disk_cap: pctOfDiskCap,
      overselling_ratio: cap > 0 ? totalAllocated / cap : 0,
    };

    const alarmsOut: ResellerCapAlarms = {
      disk_warning: pctOfDiskCap > alarms.diskWarnPct,
      disk_critical: pctOfDiskCap > alarms.diskCriticalPct,
      accounts_over_threshold: accounts
        .filter((a) => (a.disk_used_mb ?? 0) > alarms.singleAccountWarnMb)
        .map((a) => a.cpanel_username),
      // Sprint C C-2 — inode alarm fires when any account passes 70% of its
      // inode quota. cPanel typically caps individual accounts at 250k–400k;
      // writes start failing while the disk gauge can still read healthy.
      accounts_over_inode_threshold: accounts
        .filter((a) => (a.pct_of_inode_quota ?? 0) > 70)
        .map((a) => a.cpanel_username),
    };

    // Snapshot is stale if it's older than today (UTC). Cron runs at 01:30 UTC daily.
    const todayUtc = new Date().toISOString().slice(0, 10);
    const isStale = latest.snapshot_date < todayUtc;

    return {
      snapshot_date: latest.snapshot_date,
      is_stale: isStale,
      totals,
      alarms: alarmsOut,
      accounts: accounts.sort((a, b) => (b.pct_of_cap ?? 0) - (a.pct_of_cap ?? 0)),
    };
  },

  /**
   * Manually trigger the snapshot runner. Used by the "Refresh now" button.
   * EF is verify_jwt=false (cron-invoked) so no auth header needed; CORS is
   * gated by the EF's own origin check.
   */
  async refreshNow(): Promise<{ ok: boolean; upserted: number; elapsed_ms: number } | { ok: false; error: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('usage-snapshot-runner', {
        method: 'POST',
      });
      if (error) return { ok: false, error: error.message };
      return data as { ok: boolean; upserted: number; elapsed_ms: number };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  },
};
